<!-- eslint-disable no-undef -->
<template>
  <modal :allowCloseBtn="true" @close="closeModal">
    <circle-loader v-if="loading || gettingData" />
    <step v-else>
      <template v-slot:header>Confirm School Information</template>
      <div class="row">
        <div class="col-12">
          <base-form-field
            v-model="schoolInfo.schoolName"
            :schema="schema.schoolName"
            :disabled="readOnlyField"
            :read-only="readOnlyField"
          />
        </div>
      </div>
      <div class="row">
        <div class="col-12">
          <base-form-field
            v-model.string="schoolInfo.academicTerm"
            :schema="schema.academicTerm"
            :disabled="readOnlyField"
            :read-only="readOnlyField"
          />
        </div>
      </div>
      <div class="row">
        <div class="col-12">
          <base-form-field
            v-show="schoolInfo.academicTerm"
            v-model="schoolInfo.enrollmentStatusId"
            :schema="schema.enrollmentStatusId"
            :options="enrollmentStatusOptions"
          />
        </div>
      </div>
      <div class="row">
        <div class="col-6">
          <base-form-field
            v-model="schoolInfo.graduationDateMonth"
            :schema="schema.graduationDateMonth"
            :options="months()"
          >
            <template v-slot:default-option>Month</template>
          </base-form-field>
        </div>
        <div class="col-6">
          <base-form-field
            v-model="schoolInfo.graduationDateYear"
            :schema="schema.graduationDateYear"
            :options="years(true)"
          >
            <template v-slot:default-option>Year</template>
          </base-form-field>
        </div>
      </div>
      <div v-if="invalidEnrollmentTerm" class="submit-error">
        <p>Graduation Date must be after the selected Academic Period.</p>
      </div>
      <div v-if="error" class="submit-error">{{ errorMessage }}</div>
      <template v-slot:actions>
        <next-button
          :loading="loading || savingChanges"
          :disabled="loading || savingChanges"
          @click="goBackToPortal"
          text="Save Changes"
        />
      </template>
    </step>
  </modal>
</template>
<script lang="ts">
import { defineComponent, ref, reactive, computed } from "vue";
import Step from "@/components/shared/Step.vue";
import BaseFormField from "@/components/shared/BaseFormField.vue";
import { mapActions, mapState, useStore } from "vuex";
import useValidation from "@/use/validation";
import type { FormSchema, Option } from "@/models/form";
import useFormHelpers from "@/use/formHelpers";
import useFormatter from "@/use/formatter";
import CircleLoader from "@/components/shared/CircleLoader.vue";
import NextButton from "@/components/shared/NextButton.vue";
import Modal from "@/components/shared/Modal.vue";
import { ProductIds } from "@/config/constants";
import { compareMonthAndYear, createUTCDate } from "@/use/dateHelpers";
import useSchoolConfirmationForm from "@/composables/forms/useSchoolConfirmationForm";
import { EnrollmentTerm, School } from "@/models/school";

export default defineComponent({
  setup(props, { emit }) {
    const store = useStore();
    const { validateForm } = useValidation();
    const { months, years } = useFormHelpers();
    const { stringToBoolean, formatGraduationDateDisplay } = useFormatter();

    const schema = reactive({
      schoolName: {
        name: "schoolName",
        type: "readonly",
        label: "School Name",
        required: false,
      },
      academicTerm: {
        name: "academicTerm",
        type: "readonly",
        label: "Academic Term",
        required: false,
      },
      enrollmentStatusId: {
        name: "enrollmentStatusId",
        type: "select",
        label: "Enrollment Status",
        tooltipLocation: "bottom",
        required: true,
      },
      graduationDateMonth: {
        name: "graduationDateMonth",
        type: "select",
        label: "Graduation Date",
        required: true,
        errorMessage: "Grad month is required.",
      },
      graduationDateYear: {
        name: "graduationDateYear",
        type: "select",
        required: true,
        errorMessage: "Grad year is required.",
      },
    } as FormSchema);

    const error = ref<boolean>(false);
    const errorMessage = ref<string>("");
    const gettingData = ref<boolean>(true);
    const savingChanges = ref<boolean>(false);

    let schoolInfo = ref({
      school: undefined as undefined | School,
      schoolName: undefined as undefined | string,
      academicTerm: undefined as undefined | string,
      enrollmentStatus: undefined as undefined | number | string,
      enrollmentTerm: undefined as undefined | EnrollmentTerm,
      graduationDateDay: undefined as undefined | string | number,
      graduationDateMonth: undefined as undefined | string | number,
      graduationDateYear: undefined as undefined | string | number,
      graduationDate: undefined as undefined | Date,
      graduationDateDisplay: undefined as undefined | string,
      selectedEnrollmentTerm: undefined as undefined | EnrollmentTerm,
      selectedEnrollmentStatus: undefined as undefined | Option,
      schoolUndecided: undefined as undefined | boolean,
      hasSchoolConfigChanges: undefined as undefined | boolean,
    } as any);

    const {
      anticipatedGraduationDate,
      enrollmentStatuses,
      enrollmentStatusOptions,
      gradDateChanged,
      enrollmentStatusChanged,
      enrollmentTermChanged,
      hasChanges,
      invalidEnrollmentTerm,
      isValidAcademicTerm,
      loanPeriodEndDate,
      originalGradDateInfo,
      previouslySelectedEnrollmentStatus,
      previouslySelectedEnrollmentTerm,
      previouslySelectedSchool,
      selectedGraduationDate,
      selectedEnrollmentTerm,
      selectedEnrollmentStatus,
      selectedSchool,
      updatedGradDateInfo,
      validEnrollmentTerm,
    } = useSchoolConfirmationForm(schoolInfo, {
      schoolEditable: false,
      academicTermEditable: false,
      enrollmentStatusEditable: true,
    });

    const currentlyActiveModal = computed(
      () => store.getters["applicationInfoModule/ModalComponent"],
    );

    const readOnlyField = computed<boolean>(
      () => store.state.appData.application.productTypeId === ProductIds.PEL,
    );

    const initialDraw = computed(() => {
      return store.state.loanConfigurationModule.applicationInfo.pelDetails?.initialDraw;
    });

    function closeModal() {
      emit("close", true);
    }

    return {
      anticipatedGraduationDate,
      closeModal,
      compareMonthAndYear,
      createUTCDate,
      currentlyActiveModal,
      error,
      errorMessage,
      enrollmentStatusChanged,
      enrollmentTermChanged,
      enrollmentStatuses,
      enrollmentStatusOptions,
      formatGraduationDateDisplay,
      gettingData,
      gradDateChanged,
      hasChanges,
      initialDraw,
      invalidEnrollmentTerm,
      isValidAcademicTerm,
      loanPeriodEndDate,
      months,
      originalGradDateInfo,
      previouslySelectedEnrollmentStatus,
      previouslySelectedEnrollmentTerm,
      previouslySelectedSchool,
      ProductIds,
      readOnlyField,
      savingChanges,
      schema,
      schoolInfo,
      selectedEnrollmentStatus,
      selectedEnrollmentTerm,
      selectedGraduationDate,
      selectedSchool,
      store,
      stringToBoolean,
      updatedGradDateInfo,
      years,
      validateForm,
      validEnrollmentTerm,
    };
  },
  props: {
    loading: {
      type: Boolean,
      default: false,
    },
    term: {
      type: String,
    },
  },
  components: { Step, BaseFormField, CircleLoader, NextButton, Modal },
  emits: ["previous", "close", "complete"],
  data: () => ({
    path: "",
    currentSubStep: 1,
  }),
  computed: {
    ...mapState(["appData", "schools"]),
  },
  methods: {
    ...mapActions(["setAppData"]),
    async setSchoolInfo() {
      if (!this.appData.application.schoolConfiguration) {
        this.appData.application.schoolConfiguration = {};
      }

      this.schoolInfo = {
        school: this.previouslySelectedSchool || {},
      };

      this.schoolInfo.schoolName = this.appData?.application?.schoolConfiguration?.schoolName;

      this.schoolInfo.schoolId = this.appData?.application?.schoolConfiguration?.schoolId;

      this.schoolInfo.schoolUndecided =
        this.appData.application.schoolConfiguration.schoolUndecided;

      if (this.appData?.application?.schoolConfiguration?.graduationDate) {
        const date = this.createUTCDate(
          this.appData.application.schoolConfiguration.graduationDate,
        );
        const year = date.getUTCFullYear();
        const month = date.getUTCMonth() + 1;
        const day = date.getUTCDate();

        this.schoolInfo.graduationDateDay = day;
        this.schoolInfo.graduationDateMonth = month;
        this.schoolInfo.graduationDateYear = year;
        this.schoolInfo.graduationDate = date;
      }

      if (this.appData.application.schoolConfiguration?.academicTerm) {
        this.schoolInfo.enrollmentTerm = this.previouslySelectedEnrollmentTerm;
        this.schoolInfo.selectedEnrollmentTerm = this.previouslySelectedEnrollmentTerm;
        this.schoolInfo.academicTerm = this.appData.application.schoolConfiguration?.academicTerm;
      }

      if (this.appData.application.schoolConfiguration?.academicTermId) {
        this.schoolInfo.academicTermId =
          this.appData.application.schoolConfiguration?.academicTermId;
      }

      if (this.appData.application.schoolConfiguration?.enrollmentStatus) {
        this.schoolInfo.enrollmentStatusId = this.previouslySelectedEnrollmentStatus?.value;
      }

      this.gettingData = false;
    },
    goBackToPortal() {
      this.savingChanges = true;
      this.error = false;
      this.invalidEnrollmentTerm = false;
      this.errorMessage = "";
      const valid = this.validateForm(this.schema, this.schoolInfo);

      const defaultErrorMessage =
        "Something has gone wrong submitting your request. If you continue to receive this error, please contact support.";

      if (valid) {
        if (!this.appData.application.schoolConfiguration) {
          this.appData.application.schoolConfiguration = {};
        }

        if (
          this.schoolInfo.selectedEnrollmentTerm &&
          this.selectedGraduationDate &&
          this.schoolInfo?.selectedEnrollmentTerm?.fundsEndDate
        ) {
          if (this.validEnrollmentTerm) {
            this.invalidEnrollmentTerm = false;
            this.schoolInfo.graduationDate = this.selectedGraduationDate;
            this.schoolInfo.graduationDateDisplay = this.formatGraduationDateDisplay(
              this.selectedGraduationDate,
            );
            this.schoolInfo.hasSchoolConfigChanges = this.hasChanges;

            this.$emit("complete", this.schoolInfo);
          } else {
            this.invalidEnrollmentTerm = true;
            this.savingChanges = false;
          }
        } else {
          this.error = true;
          this.errorMessage = defaultErrorMessage;
        }
      } else {
        this.savingChanges = false;
      }
    },
  },
  async mounted() {
    this.savingChanges = false;
    await this.setSchoolInfo();
  },
});
</script>

<style lang="scss" scoped>
.btn {
  display: inline-block;
  text-align: center;
  vertical-align: middle;
  border: 1px solid transparent;
  border-radius: 6px;
  font-size: 1em;
  font-weight: 600;
  line-height: 1.5em;
  white-space: normal;
  padding: 0.715em 0.841em;
  cursor: pointer;

  &:first-child {
    margin-right: 20px;
  }

  &:focus,
  &:hover {
    filter: brightness(90%) saturate(90%);
  }

  &.btn-primary {
    background: var(--theme-color);
    color: #fff;
    min-width: 150px;
    max-width: 100%;
  }

  &:disabled {
    opacity: 0.65;
    cursor: default;

    &:focus,
    &:hover {
      filter: none;
    }
  }
}
.row {
  margin-top: 1rem;
}

.submit-error {
  padding: 10px 1%;
  margin: 10px auto;
  border: 1px solid lightgray;
  border-radius: 0.6rem;
  max-width: 85%;
}

.submit-error p {
  text-align: center;
}
.submit-error,
.submit-error {
  border-color: red;
  color: red;
  font-weight: 700;
  margin-top: 30px;
}
.submit-error ul,
.submit-error ul {
  margin-bottom: 0;
  list-style: none;
}

@media (max-width: 576px) {
  .submit-error {
    max-width: 100%;
  }
}
</style>
